import React, { forwardRef } from 'react';

import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import { Anchor, Box, Inline, xcss } from '@atlaskit/primitives';

import { type DiscoveryCTALinkButtonProps } from './types';

const baseStyles = xcss({
	display: 'inline-flex',
	paddingRight: 'space.150',
	paddingLeft: 'space.100',
	textDecoration: 'none',
	borderRadius: 'border.radius.100',
	justifyContent: 'center',
	alignItems: 'center',
	width: 'fit-content',
	paddingBlock: 'space.0',
	transition: 'background 0.1s ease-out',
	whiteSpace: 'nowrap',
});

const defaultSpacingStyles = xcss({
	minHeight: 'size.300',
});
const compactSpacingStyles = xcss({
	minHeight: 'size.200',
});

const defaultStyles = xcss({
	color: 'color.text.inverse',
	backgroundColor: 'color.background.accent.purple.bolder',
	':hover': {
		backgroundColor: 'color.background.accent.purple.bolder.hovered',
		textDecoration: 'none',
		color: 'color.text.inverse',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.bolder.pressed',
		textDecoration: 'none',
		color: 'color.text.inverse',
	},
});

const invertedStyles = xcss({
	color: 'color.text.discovery',
	backgroundColor: 'elevation.surface',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border.accent.purple',
	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
		textDecoration: 'none',
		color: 'color.text.discovery',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
		textDecoration: 'none',
		color: 'color.text.discovery',
	},
});

const subtleStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	color: 'color.text.accent.purple',
	borderRadius: 'border.radius',
	whiteSpace: 'nowrap',
	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
	},
});

const contentContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.050',
});

const iconContainerStyles = xcss({
	display: 'flex',
	width: 'size.200',
	height: 'size.200',
	justifyContent: 'center',
	alignItems: 'center',
});

export const DiscoveryCTALinkButton = forwardRef<HTMLAnchorElement, DiscoveryCTALinkButtonProps>(
	({ linkButtonStyle, href, onClick, target, children, testId, key, spacing }, ref) => {
		return (
			<Anchor
				ref={ref}
				testId={testId}
				key={key}
				href={href}
				onClick={onClick}
				target={target}
				xcss={[
					baseStyles,
					linkButtonStyle === 'default' && defaultStyles,
					linkButtonStyle === 'inverted' && invertedStyles,
					linkButtonStyle === 'subtle' && subtleStyles,
					spacing === 'compact' ? compactSpacingStyles : defaultSpacingStyles,
				]}
			>
				<Box xcss={contentContainerStyles}>
					<Box xcss={iconContainerStyles}>
						<EditionsIcon label="" />
					</Box>
					<Inline>{children}</Inline>
				</Box>
			</Anchor>
		);
	},
);
